/**
 * Mapping a planet's longitude onto the price axis, and stacking the result into octaves.
 *
 * <h2>What the overlay actually carries</h2>
 * By the time an overlay reaches the chart its values are already PRICES, not degrees. App.js
 * unwraps the longitude series and multiplies by `pricePer360 / 360` before storing it. That is the
 * single fact the renderer has to get right, and getting it wrong is subtle rather than obvious:
 * treating a price as though it were still degrees produces a line that looks plausible, moves with
 * the planet, and is wrong.
 *
 * The renderer used to do exactly that — `(value % 360) * pricePer360 / 360` — which scaled by
 * `pricePer360/360` a second time and took a modulus at 360 PRICE units. With pricePer360 = 200 and
 * Venus, the plotted sawtooth repeated every 405 days against a 225-day orbit, and dropped the full
 * height of a band at a moment when Venus was at 288 degrees: nowhere near Aries, and nothing to do
 * with the planet at all. Reducing the price modulo `pricePer360` instead gives back exactly the
 * fractional position in the turn, so one band is one revolution.
 */

/** Positive modulus. A retrograde run can unwrap below zero, where JS `%` returns a negative. */
function mod(value, m) {
  if (!(m > 0)) return 0;
  return ((value % m) + m) % m;
}

/**
 * Where a longitude-derived price sits inside one octave, before the octave offset is added.
 *
 * @param {number} value    the overlay's value: an unwrapped longitude already scaled to price
 * @param {number} pricePer360 price units spanned by one full revolution
 * @returns {number} a value in [0, pricePer360)
 */
export function positionInBand(value, pricePer360) {
  return mod(value, pricePer360);
}

/**
 * The octave indices needed to cover a price range.
 *
 * Each octave is one full revolution of the planet, `pricePer360` tall, stacked from `basePrice`.
 * Enough of them are returned to cover `basePrice` up to `topPrice`, so a chart at SPY's $500 gets
 * the whole axis without the user adding one instance per band by hand.
 *
 * @returns {number[]} octave indices, ascending, always at least [0]
 */
export function autoOctaves(basePrice, topPrice, pricePer360) {
  if (!(pricePer360 > 0) || !Number.isFinite(basePrice) || !Number.isFinite(topPrice)) return [0];
  const span = topPrice - basePrice;
  if (!(span > 0)) return [0];
  // +1 so the topmost band covers the axis rather than stopping just under it.
  const count = Math.ceil(span / pricePer360) + 1;
  return Array.from({ length: count }, (_, k) => k);
}

/**
 * The octave indices a user asked for explicitly, as an inclusive range.
 *
 * Falls back to the automatic set when the range is missing or nonsensical — a reversed or
 * unparseable range should not silently plot nothing.
 */
export function explicitOctaves(nStart, nEnd) {
  // A cleared number field gives '', and Number('') is 0 — finite, so it would quietly be read as
  // octave zero. An empty box means "not specified", which falls back to auto-fit.
  const blank = v => v === null || v === undefined || String(v).trim() === '';
  if (blank(nStart) || blank(nEnd)) return null;

  const from = Number(nStart);
  const to = Number(nEnd);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  const lo = Math.min(Math.trunc(from), Math.trunc(to));
  const hi = Math.max(Math.trunc(from), Math.trunc(to));
  // A guard against a typo asking for thousands of series.
  if (hi - lo > 50) return null;
  return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
}

/**
 * The octaves to draw, honouring the overlay's auto-fit setting.
 *
 * Auto-fit is the default: an overlay saved before these controls existed carries neither flag and
 * should keep behaving as it did, which was to cover the chart automatically.
 */
export function octavesFor(overlay, basePrice, topPrice, pricePer360) {
  const autoFit = overlay?.autoFitOctaves !== false;
  if (!autoFit) {
    const explicit = explicitOctaves(overlay.octaveStart, overlay.octaveEnd);
    if (explicit) return explicit;
  }
  return autoOctaves(basePrice, topPrice, pricePer360);
}

/**
 * One octave's series, with the wrap at Aries left as a gap rather than drawn.
 *
 * A modular band has to reset once per revolution: the planet leaves 359.99 degrees and re-enters
 * at 0, so the price jumps from the top of the band to the bottom. Joining those two points draws a
 * vertical line the full height of the band straight down the chart — the "cliff". ECharts breaks a
 * line wherever a point's value is null, so emitting a null at the reset gives one clean path per
 * revolution instead.
 *
 * The gap replaces nothing: it is inserted between the last point of one revolution and the first
 * of the next, so no bar loses its value.
 *
 * @param {string[]} dates    x values, one per point
 * @param {number[]} values   overlay values (unwrapped longitude already scaled to price)
 * @param {number}   pricePer360
 * @param {number}   basePrice bottom of the lowest band
 * @param {number}   octave    which band, 0 being the lowest
 * @returns {Array} ECharts [x, y] pairs, with [x, null] at each revolution boundary
 */
export function bandSeries(dates, values, pricePer360, basePrice, octave) {
  const out = [];
  const offset = basePrice + octave * pricePer360;
  let previous = null;

  for (let i = 0; i < dates.length; i++) {
    const value = values[i];
    if (!Number.isFinite(value)) {
      out.push([dates[i], null]);
      previous = null;
      continue;
    }
    const position = positionInBand(value, pricePer360);

    // A drop of more than half a band between neighbouring points is the wrap, not motion: no
    // planet covers half a revolution in one day. Retrograde motion crossing the boundary the
    // other way jumps upward by the same amount, and breaks the line for the same reason.
    if (previous !== null && Math.abs(position - previous) > pricePer360 / 2) {
      out.push([dates[i], null]);
    }
    out.push([dates[i], offset + position]);
    previous = position;
  }
  return out;
}
